import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import styled from 'styled-components';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Link,
  TextField,
} from '@material-ui/core';
import PaymentSuccessDialog from './PaymentSuccessDialog';

const Field = styled.div`
  margin-bottom: 14px;
`;

const CardShell = styled.div`
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
`;

const SubmitBtn = styled.button`
  width: 100%;
  height: 40px;
  border: 1px solid #111827;
  background: #111827;
  color: #ffffff;
  border-radius: 8px;
  font-weight: 600;
  letter-spacing: 0.2px;

  &:disabled {
    background: #e5e7eb;
    border-color: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;
`;

const Note = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 8px;
`;

const apiBaseUrl = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/$/, '')
  : process.env.NODE_ENV === 'development'
  ? 'http://localhost:3008'
  : 'https://yielding-linnell-ev-payment-a515489f.koyeb.app';

const StripePaymentForm = ({
  price,
  description = 'Computer service / Training session',
  basePrice,
  tax = 0,
  autoCheckTerms = false,
  currency = 'eur',
  metadata: extraMetadata,
  prefillEmail = '',
  prefillName = '',
  hideTaxBreakdown = false,
  onSuccess,
  successCtaUrl,
  successCtaLabel,
  // Recurring mode: create a Stripe Subscription instead of a one-off
  // PaymentIntent. The confirm step is identical — the client secret we get
  // back belongs to the subscription's first invoice — but the card is stored
  // as the subscription's default payment method for later cycles.
  subscriptionMode = false,
  intervalMonths = 1,
}) => {
  // If basePrice is not provided, use price as basePrice (backwards compatibility)
  const actualBasePrice = basePrice !== undefined ? basePrice : price;
  const currencySymbol = currency === 'usd' ? '$' : '€';
  const currencyName = currency === 'usd' ? 'USD' : 'EUR';
  const stripe = useStripe();
  const elements = useElements();

  const [billingDetails, setBillingDetails] = useState({
    name: prefillName || '',
    email: prefillEmail || '',
  });
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // Recurring terms are no longer shown expanded (owner's decision), but they
  // must still be reachable at the payment step — that is where Stripe's
  // subscription rules and the EU Consumer Rights Directive attach the duty.
  const [showSubTerms, setShowSubTerms] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);

  // Auto-check terms when autoCheckTerms prop is true or when description changes
  useEffect(() => {
    if (autoCheckTerms) {
      setAcceptedTerms(true);
    }
  }, [autoCheckTerms, description]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError('Payment system not initialized. Please refresh.');
      return;
    }
    if (!acceptedTerms) {
      setError('You must accept the Terms & Conditions');
      return;
    }
    if (billingDetails.name.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(billingDetails.email)) {
      setError('Invalid email format');
      return;
    }
    const minAmount = currency === 'usd' ? 0.5 : 0.5;
    if (price < minAmount) {
      setError(`Amount must be at least ${currencySymbol}${minAmount.toFixed(2)}`);
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      const createUrl = subscriptionMode
        ? `${apiBaseUrl}/api/euroview/create-subscription`
        : `${apiBaseUrl}/api/create-stripe-payment-intent`;

      const createRes = await fetch(createUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Math.round(price * 100),
          currency: currency,
          billingDetails,
          metadata: {
            service_description: description,
            terms_accepted: true,
            ...(extraMetadata || {}),
          },
        }),
      });

      const data = await createRes.json();
      if (!createRes.ok) throw new Error(data.error || 'Failed to create payment');

      const { clientSecret, paymentIntentId } = data;

      const { error: stripeErr, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { name: billingDetails.name, email: billingDetails.email },
        },
        receipt_email: billingDetails.email,
      });

      if (stripeErr) throw new Error(stripeErr.message);

      if (paymentIntent.status === 'succeeded') {
        setShowSuccessDialog(true);
        setConfirmationLoading(true);

        // Optional server confirmation. Skipped for subscriptions: the first
        // invoice's PaymentIntent is already confirmed above, and re-confirming
        // it is not a supported operation.
        if (subscriptionMode) {
          setConfirmationLoading(false);
        } else {
          fetch(`${apiBaseUrl}/api/confirm-stripe-payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              paymentIntentId,
              paymentMethodId: paymentIntent.payment_method,
              receiptEmail: billingDetails.email,
            }),
          }).finally(() => setConfirmationLoading(false));
        }

        // Reset form
        setBillingDetails({ name: '', email: '' });
        elements.getElement(CardElement).clear();
        setAcceptedTerms(false);

        if (typeof onSuccess === 'function') {
          try {
            onSuccess({ paymentIntentId, amount: price, currency });
          } catch (e) {
            // ignore
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Payment failed. Try again.');
    } finally {
      setProcessing(false);
    }
  };

  const TermsContent = () => (
    <>
      <DialogTitle>Terms &amp; Conditions</DialogTitle>
      <DialogContent dividers>
        <Typography variant='h6' gutterBottom>
          Scope of Service
        </Typography>
        <Typography paragraph>
          You are purchasing professional <strong>computer services and/or online programming lessons</strong>. No physical goods are sold. Work is
          delivered remotely via video calls, screen-share, or asynchronous support.
        </Typography>

        <Typography variant='h6' gutterBottom>
          Use &amp; Compliance
        </Typography>
        <Typography paragraph>
          You agree not to use our services for any <strong>illegal</strong> purpose or to support restricted activities. For details, see{' '}
          <Link href='https://stripe.com/restricted-businesses' target='_blank' rel='noopener noreferrer'>
            Stripe’s Restricted Businesses
          </Link>
          .
        </Typography>

        <Typography variant='h6' gutterBottom>
          Scheduling, Cancellations &amp; Refunds
        </Typography>
        <Typography component='div'>
          <ul>
            <li>Sessions are booked by the hour unless otherwise agreed in writing.</li>
            <li>
              Free cancellations or rescheduling up to <strong>24 hours</strong> before the session.
            </li>
            <li>
              Within 24 hours, cancellations may be charged up to <strong>100% of the booked hour(s)</strong>.
            </li>
            <li>
              Completed time is <strong>non-refundable</strong>; unused prepaid time can be rescheduled within 60 days.
            </li>
          </ul>
        </Typography>

        <Typography variant='h6' gutterBottom>
          Liability
        </Typography>
        <Typography paragraph>
          Best-effort guidance is provided, but results depend on your environment and implementation. Our liability is limited to the amount paid for
          the specific session.
        </Typography>

        <Typography variant='h6' gutterBottom>
          Payment Processor
        </Typography>
        <Typography paragraph>
          Payments are processed by Stripe. By paying, you accept the applicable Stripe terms and acknowledge processing under{' '}
          <Link href='https://stripe.com/privacy' target='_blank' rel='noopener noreferrer'>
            Stripe’s Privacy Policy
          </Link>
          .
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTermsOpen(false)} color='primary'>
          Close
        </Button>
      </DialogActions>
    </>
  );

  const PrivacyContent = () => (
    <>
      <DialogTitle>Privacy Policy</DialogTitle>
      <DialogContent dividers>
        <Typography paragraph>
          We collect your name and email to schedule, deliver service, and send receipts. Payment details are handled by Stripe — we do not store card
          numbers.
        </Typography>
        <Typography component='div'>
          <ul>
            <li>Data is retained only as long as needed for accounting and legal requirements.</li>
            <li>You may request access, correction, or deletion by contacting us at the email below.</li>
          </ul>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPrivacyOpen(false)} color='primary'>
          Close
        </Button>
      </DialogActions>
    </>
  );

  return (
    <>
      <Dialog open={termsOpen} onClose={() => setTermsOpen(false)} maxWidth='md' fullWidth>
        <TermsContent />
      </Dialog>
      <Dialog open={privacyOpen} onClose={() => setPrivacyOpen(false)} maxWidth='sm' fullWidth>
        <PrivacyContent />
      </Dialog>

      {error && (
        <div
          style={{
            color: '#b91c1c',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            padding: '10px',
            borderRadius: 8,
            marginBottom: 12,
          }}>
          {error}
        </div>
      )}

      <PaymentSuccessDialog
        open={showSuccessDialog}
        loading={confirmationLoading}
        onClose={() => setShowSuccessDialog(false)}
        ctaUrl={successCtaUrl}
        ctaLabel={successCtaLabel}
      />

      <form onSubmit={handleSubmit}>
        <Field>
          <TextField
            fullWidth
            label='Full name'
            variant='outlined'
            size='small'
            value={billingDetails.name}
            onChange={(e) => setBillingDetails({ ...billingDetails, name: e.target.value })}
            required
          />
        </Field>

        <Field>
          <TextField
            fullWidth
            label='Email'
            type='email'
            variant='outlined'
            size='small'
            value={billingDetails.email}
            onChange={(e) => setBillingDetails({ ...billingDetails, email: e.target.value })}
            required
          />
        </Field>

        <CardShell>
          <CardElement options={{ style: { base: { fontSize: '16px' } }, hidePostalCode: true }} />
        </CardShell>

        <div style={{ marginTop: 12 }}>
          <FormControlLabel
            control={<Checkbox checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} color='primary' />}
            label={
              <Typography variant='body2'>
                I agree to the{' '}
                <Link
                  href='#'
                  onClick={(e) => {
                    e.preventDefault();
                    setTermsOpen(true);
                  }}>
                  Terms &amp; Conditions
                </Link>{' '}
                and{' '}
                <Link
                  href='#'
                  onClick={(e) => {
                    e.preventDefault();
                    setPrivacyOpen(true);
                  }}>
                  Privacy Policy
                </Link>
                .
                {/* Recurring terms. Icon-only and inline, at the owner's request.
                    It keeps an accessible name and a tooltip, so hover and screen
                    readers both announce "Subscription terms" — a control with no
                    name would disclose nothing at all. stopPropagation keeps the
                    click from toggling the checkbox it sits inside. */}
                {subscriptionMode && (
                  <>
                    {' '}
                    <Link
                      href='#'
                      role='button'
                      aria-expanded={showSubTerms}
                      aria-label='Subscription terms'
                      title='Subscription terms'
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowSubTerms((v) => !v);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 15,
                        height: 15,
                        borderRadius: '50%',
                        border: '1px solid currentColor',
                        fontSize: 10,
                        fontWeight: 700,
                        lineHeight: 1,
                        verticalAlign: 'middle',
                        textDecoration: 'none'
                      }}>
                      i
                    </Link>
                  </>
                )}
              </Typography>
            }
          />

          {subscriptionMode && showSubTerms && (
            <div
              style={{
                marginTop: 8,
                padding: '8px 10px',
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 12,
                lineHeight: 1.5,
                color: '#374151',
              }}>
              {currencySymbol}
              {price.toFixed(2)} is charged today, then {currencySymbol}
              {price.toFixed(2)}{' '}
              {intervalMonths === 1
                ? 'every month'
                : intervalMonths === 12
                  ? 'every year'
                  : `every ${intervalMonths} months`}{' '}
              automatically, until you cancel. You can cancel any time from your EuroView
              profile — your access continues to the end of the period you have already paid
              for.
            </div>
          )}
        </div>

        <Row>
          <Typography variant='body2'>Service</Typography>
          <Typography variant='body2'>
            {currencySymbol}
            {actualBasePrice.toFixed(2)}
          </Typography>
        </Row>
        {tax > 0 && !hideTaxBreakdown && (
          <Row>
            <Typography variant='body2'>Tax (10%)</Typography>
            <Typography variant='body2'>
              {currencySymbol}
              {tax.toFixed(2)}
            </Typography>
          </Row>
        )}
        <Row>
          <Typography variant='body2' style={{ fontWeight: 600 }}>
            Total
          </Typography>
          <Typography variant='body2' style={{ fontWeight: 600 }}>
            {currencySymbol}
            {price.toFixed(2)}
          </Typography>
        </Row>

        {/* REMOVED 20 Aug 2026 at the site owner's request — auto-renewal is
            disclosed and ticked on euroview-iptv.com before the customer reaches
            checkout, so this repeat was considered redundant. Kept verbatim so it
            can be restored if Stripe or an EU consumer-law review asks for the
            terms to appear at the payment step itself.

        {subscriptionMode && (
          <div
            style={{
              marginTop: 12,
              padding: '10px 12px',
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              fontSize: 12,
              lineHeight: 1.5,
              color: '#374151',
            }}>
            <strong>This is a recurring subscription.</strong> You pay{' '}
            {currencySymbol}{price.toFixed(2)} today, then {currencySymbol}{price.toFixed(2)}{' '}
            {intervalMonths === 1
              ? 'every month'
              : intervalMonths === 12
                ? 'every year'
                : `every ${intervalMonths} months`}{' '}
            automatically, until you cancel. You can cancel any time from your EuroView profile —
            your access continues to the end of the period you have already paid for.
          </div>
        )}
        */}

        <SubmitBtn type='submit' disabled={!stripe || processing || !acceptedTerms}>
          {/* Price only. Was `Subscribe — €X / month` in subscription mode;
              changed 20 Aug 2026 at the site owner's request. */}
          {processing ? 'Processing…' : `${currencySymbol}${price.toFixed(2)}`}
        </SubmitBtn>
        <Note>
          {subscriptionMode
            ? 'Payments and recurring billing are securely handled by Stripe. Your card is stored by Stripe, never by us.'
            : 'Payments are securely processed by Stripe.'}
        </Note>
      </form>
    </>
  );
};

export default StripePaymentForm;
